import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, CircularProgress, tableCellClasses, styled, FormGroup, FormControlLabel,
  Checkbox, Button
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import api from '../api';
import { formatErrorMessage } from '../utils';
import { getConfig } from '../config';

const { COMPANY_NAME } = getConfig();

interface Payment {
  id: number;
  amount: number;
  due_date: string;
}

interface SaleItem {
  id: number;
  item: string;
  amount: number;
  release_date?: string;
  state?: string;
  completion_date?: string;
  payments?: Payment[];
}

interface Sale {
  id: number;
  contract_date: string;
  company: { id: number; name: string };
  site: { id: number; name: string };
  total_amount: number;
  items: SaleItem[];
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontSize: 14,
    whiteSpace: 'nowrap',
    textAlign: 'center'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    whiteSpace: 'nowrap',
    borderRight: '1px solid rgba(224, 224, 224, 1)',
    '&:last-child': {
      borderRight: 'none'
    }
  }
}));

const months = Array.from({ length: 12 }, (_, i) => i + 1);

const SalesPlanning: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<number[]>(months);
  const [foldedSaleIds, setFoldedSaleIds] = useState<number[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const currentYear = new Date().getFullYear();
  const printAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => { fetchSales(); }, []);

  const fetchSales = async () => {
    try {
      const response = await api.get('/api/sales');
      const sorted = response.data.sort((a: Sale, b: Sale) => a.company.name.localeCompare(b.company.name));
      setSales(sorted);
    } catch (error) {
      enqueueSnackbar(formatErrorMessage(error), { variant: 'error' });
    }
  };

  const expandAll = () => setFoldedSaleIds([]);
  const collapseAll = () => setFoldedSaleIds(sales.map(s => s.id));

  const toggleSale = (saleId: number) => {
    setFoldedSaleIds(prev => prev.includes(saleId) ? prev.filter(id => id !== saleId) : [...prev, saleId]);
  };

  const formatAmount = (amount: number) => amount.toLocaleString();

  const getPlannedMonth = (d?: string) => {
    if (!d) return null;
    const date = new Date(d);
    return date.getDate() <= 15 ? date.getMonth() + 1 : (date.getMonth() + 2) % 12 || 12;
  };
  const getPlannedAmountByMonth = (item: SaleItem, month: number): number => {
    let total = 0;

    // 1. 분할된 금액 중 해당 월에 due_date가 있는 것 합산
    for (const p of item.payments || []) {
      const d = new Date(p.due_date);
      if (d.getFullYear() === currentYear && d.getMonth() + 1 === month) {
        total += p.amount;
      }
    }

    // 2. 분할 외의 잔여 금액은 release_date 기준으로 포함
    const splitTotal = item.payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
    const remaining = item.amount - splitTotal;

    if (remaining > 0 && item.release_date) {
      const d = new Date(item.release_date);
      const pm = getPlannedMonth(item.release_date);
      if (pm === month && d.getFullYear() === currentYear) {
        total += remaining;
      }
    }

    return total;
  };
  const handlePrint = () => {
    const printContents = printAreaRef.current?.innerHTML;
    if (printContents) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`<!DOCTYPE html><html><head><title>프린트</title><style>
          body { font-family: Arial; margin: 0; padding: 10px; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid blac  k; padding: 4px; text-align: center; font-size: 12px; }
          th { background-color: #000; color: white; }
        </style></head><body>
        <h2 style="text-align: center">${COMPANY_NAME} ${currentYear}년 매출계획</h2>
        <div style="text-align: right; margin-bottom: 10px;">${new Date().toLocaleDateString()} (단위: 1000원)</div>
        ${printContents}</body></html>`);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
    }
  };
  const getActualAmountByMonth = (item: SaleItem, month: number): number => {
    let total = 0;
    const split = item.payments || [];

    for (const p of split) {
      const d = new Date(p.due_date);
      const pm = d.getMonth() + 1;
      if (pm === month && d.getFullYear() === currentYear) total += p.amount;
    }

    const paid = split.reduce((s, p) => s + p.amount, 0);
    const remaining = item.amount - paid;

    if (remaining > 0 && item.completion_date) {
      const d = new Date(item.completion_date);
      if (d.getMonth() + 1 === month && d.getFullYear() === currentYear) {
        total += remaining;
      }
    }

    return total;
  };

  const getCarryOverAmount = (items: SaleItem[]) => {
    return items.reduce((sum, i) => {
      if (i.state === '계산서발행' && i.completion_date) {
        const y = new Date(i.completion_date).getFullYear();
        return y < currentYear ? sum + i.amount : sum;
      }
      return sum;
    }, 0);
  };

  const getYearlyActualAmount = (items: SaleItem[]) => {
    return months.reduce((sum, m) => sum + items.reduce((s, i) => s + getActualAmountByMonth(i, m), 0), 0);
  };

  const getItemActualTotal = (item: SaleItem) => months.reduce((sum, m) => sum + getActualAmountByMonth(item, m), 0);
  const totalAmount = sales.reduce((sum, s) => sum + s.total_amount, 0);
  const totalCarryOver = sales.reduce((sum, s) => sum + getCarryOverAmount(s.items), 0);
  const totalActual = sales.reduce((sum, s) => sum + getYearlyActualAmount(s.items), 0);
  const totalRemain = totalAmount - totalCarryOver - totalActual;

  return (
    <Box>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">{COMPANY_NAME} {currentYear}년 매출계획</Typography>
          <Box>
            <Button variant="outlined" onClick={expandAll} sx={{ mr: 1 }}>모두 열기</Button>
            <Button variant="outlined" onClick={collapseAll} sx={{ mr: 1 }}>모두 닫기</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => { }} sx={{ mr: 1 }}>CSV 다운로드</Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>프린트</Button>
          </Box>
        </Box>

        <FormGroup row sx={{ mb: 2 }}>
          <FormControlLabel control={<Checkbox checked={selectedMonths.length === 12} onChange={(e) => setSelectedMonths(e.target.checked ? months : [])} />} label="전체 선택" />
          {months.map((m) => (
            <FormControlLabel key={m} control={<Checkbox checked={selectedMonths.includes(m)} onChange={() => setSelectedMonths(prev => prev.includes(m) ? prev.filter(i => i !== m) : [...prev, m])} />} label={`${m}월`} />
          ))}
        </FormGroup>

        <Box ref={printAreaRef}> {/* table contents remain same */} </Box>
      </Box>
      <Box ref={printAreaRef}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>계약일</StyledTableCell>
                <StyledTableCell>업체명</StyledTableCell>
                <StyledTableCell>현장명</StyledTableCell>
                <StyledTableCell>내용</StyledTableCell>
                <StyledTableCell>계약금액</StyledTableCell>
                <StyledTableCell>이월금액</StyledTableCell>
                <StyledTableCell>{currentYear} 누계</StyledTableCell>
                <StyledTableCell>수주잔액</StyledTableCell>
                {selectedMonths.map(m => <StyledTableCell key={m}>{m}월</StyledTableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {sales.map(sale => {
                const folded = foldedSaleIds.includes(sale.id);
                const carry = getCarryOverAmount(sale.items);
                const actual = getYearlyActualAmount(sale.items);
                const remain = sale.total_amount - carry - actual;

                return (
                  <React.Fragment key={sale.id}>
                    <TableRow onClick={() => toggleSale(sale.id)} sx={{ backgroundColor: '#eee', cursor: 'pointer' }}>
                      <StyledTableCell>{sale.contract_date}</StyledTableCell>
                      <StyledTableCell>{sale.company.name}</StyledTableCell>
                      <StyledTableCell>{sale.site.name}</StyledTableCell>
                      <StyledTableCell>소계</StyledTableCell>
                      <StyledTableCell align="right">{formatAmount(sale.total_amount)}</StyledTableCell>
                      <StyledTableCell align="right">{formatAmount(carry)}</StyledTableCell>
                      <StyledTableCell align="right">{formatAmount(actual)}</StyledTableCell>
                      <StyledTableCell align="right">{formatAmount(remain)}</StyledTableCell>
                      {selectedMonths.map(m => {
                        const p = sale.items.reduce((s, i) => s + getPlannedAmountByMonth(i, m), 0);
                        const a = sale.items.reduce((s, i) => s + getActualAmountByMonth(i, m), 0);

                        return (
                          <StyledTableCell key={m} align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Box sx={{ flex: 1, textAlign: 'center', borderRight: '1px solid #ddd' }}>
                                {p > 0 ? formatAmount(p) : '-'}
                              </Box>
                              <Box sx={{ flex: 1, textAlign: 'center' }}>
                                {a > 0 ? formatAmount(a) : '-'}
                              </Box>
                            </Box>
                          </StyledTableCell>
                        );
                      })}
                    </TableRow>
                    {!folded && sale.items.map(item => (
                      <TableRow key={item.id}>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>{item.item}</StyledTableCell>
                        <StyledTableCell align="right">{formatAmount(item.amount)}</StyledTableCell>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell align="right">{formatAmount(getItemActualTotal(item))}</StyledTableCell>
                        <StyledTableCell align="right">{formatAmount(item.amount - getItemActualTotal(item))}</StyledTableCell>
                        {selectedMonths.map(m => (
                          <StyledTableCell key={m} align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                              <Box sx={{ flex: 1, textAlign: 'center', borderRight: '1px solid #ddd' }}>{getPlannedAmountByMonth(item, m) > 0 ? formatAmount(getPlannedAmountByMonth(item, m)) : '-'}</Box>
                              <Box sx={{ flex: 1, textAlign: 'center' }}>{getActualAmountByMonth(item, m) > 0 ? formatAmount(getActualAmountByMonth(item, m)) : '-'}</Box>
                            </Box>
                          </StyledTableCell>
                        ))}
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
              <TableRow>
                <StyledTableCell colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>합계</StyledTableCell>
                <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatAmount(totalAmount)}</StyledTableCell>
                <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatAmount(totalCarryOver)}</StyledTableCell>
                <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatAmount(totalActual)}</StyledTableCell>
                <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatAmount(totalRemain)}</StyledTableCell>
                {selectedMonths.map(m => {
                  const p = sales.reduce((s, sale) => s + sale.items.reduce((s2, i) => s2 + getPlannedAmountByMonth(i, m), 0), 0);
                  const a = sales.reduce((s, sale) => s + sale.items.reduce((s2, i) => s2 + getActualAmountByMonth(i, m), 0), 0);
                  return (
                    <StyledTableCell key={m} align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box sx={{ flex: 1, textAlign: 'center', borderRight: '1px solid #ddd' }}>{p > 0 ? formatAmount(p) : '-'}</Box>
                        <Box sx={{ flex: 1, textAlign: 'center' }}>{a > 0 ? formatAmount(a) : '-'}</Box>
                      </Box>
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SalesPlanning;